'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  image: string;
  alt: string;
  rating: number;
  text: string;
  service: string;
  result: string;
}

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
}

export default function TestimonialsSection({ testimonials }: TestimonialsSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-16 lg:py-24 bg-cornsilk">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-headline font-bold text-3xl sm:text-4xl text-primary mb-4">
            Customer Success Stories
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Real results from real Phoenix Metro homeowners who trust us with their outdoor living spaces.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-brand-elevated p-8 lg:p-12">
            <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
              <div className="relative w-32 h-32 rounded-full overflow-hidden flex-shrink-0 shadow-brand">
                <AppImage
                  src={currentTestimonial.image}
                  alt={currentTestimonial.alt}
                  fill
                  className="object-cover"
                />
              </div>

              <div className="flex-1 text-center md:text-left">
                <div className="flex justify-center md:justify-start mb-3">
                  {[...Array(5)].map((_, index) => (
                    <Icon
                      key={index}
                      name="StarIcon"
                      variant={index < currentTestimonial.rating ? 'solid' : 'outline'}
                      size={20}
                      className={index < currentTestimonial.rating ? 'text-accent' : 'text-gray-300'}
                    />
                  ))}
                </div>

                <p className="text-lg text-foreground font-body italic mb-6 leading-relaxed">
                  "{currentTestimonial.text}"
                </p>

                <div className="mb-4">
                  <h4 className="font-headline font-bold text-lg text-foreground">
                    {currentTestimonial.name}
                  </h4>
                  <p className="text-sm text-muted-foreground font-body">
                    {currentTestimonial.location}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs text-primary font-body font-semibold uppercase tracking-wide mb-1">
                      Service
                    </p>
                    <p className="text-sm text-foreground font-body">
                      {currentTestimonial.service}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-success font-body font-semibold uppercase tracking-wide mb-1">
                      Result
                    </p>
                    <p className="text-sm text-foreground font-body">
                      {currentTestimonial.result}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-4 mt-8">
              <button
                onClick={handlePrevious}
                className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-brand shadow-brand"
                aria-label="Previous testimonial"
              >
                <Icon name="ChevronLeftIcon" size={20} />
              </button>

              <div className="flex space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-brand ${
                      index === currentIndex ? 'bg-primary w-8' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={handleNext}
                className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-brand shadow-brand"
                aria-label="Next testimonial"
              >
                <Icon name="ChevronRightIcon" size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}